<template>
  <div class="podcast-section-contents">
    <template v-if="trackUrl">
      <p v-if="someContentIsExceeding" class="mb-10 text-error">
        La traccia audio è stata modificata e alcuni contenuti sforano la
        lunghezza della traccia.
        <br />
        Aggiornali trascinando il cursore o rimuovili dalla lista dei contenuti.
      </p>
      <p class="pb-5 text-gray-800">
        Trascina il cusore per individuare il punto in cui aggiungere un
        contenuto
      </p>

      <AudioPlayer
        v-if="trackUrl"
        :src="trackUrl"
        :visual-elements="modelData.data"
        @move="updateVisualElementTime"
        @reorder="reorderVisualElements"
        @time="currentTime = $event"
        @durationSaved="duration = $event"
      />

      <div class="podcast-section-contents__add py-8">
        <button class="btn-round-icon-outline" @click="addVisualElement()">
          <IconPlus />
        </button>
        <span class="text-sans-base ml-2 text-gray-800"
          >Aggiungi contenuto</span
        >
      </div>

      <div class="podcast-section-contents__elements">
        <VisualElementsRepeater
          v-for="(visualElement, index) of modelData.data"
          :index="index"
          :key="visualElement._id"
          :element="visualElement"
          :is-exceeding="getIsExceeding(visualElement)"
          @updateLabel="updateVisualElementLabel($event, visualElement._id)"
          @update="updateVisualElementType($event, visualElement._id)"
          @delete="deleteVisualElement(visualElement._id)"
          ref="repeaters"
        >
          <template slot-scope="{ type }">
            <component
              :is="`visual-element-type-${type}`"
              :podcastId="id"
              v-bind="visualElement"
              @update="updateVisualElement($event, visualElement._id)"
              @valid="updateValidity($event, visualElement._id)"
            ></component>
          </template>
        </VisualElementsRepeater>
      </div>
    </template>
    <div v-else-if="loading" class="text-gray-600">
      Loading...
    </div>

    <div v-else class="text-gray-600">
      Devi caricare una traccia audio prima di poter inserire i contenuti
    </div>
  </div>
</template>

<script>
/** Query */
import DELETE_VISUAL_ELEMENT from '@/graphql/DeletePodcastVisualElement.gql'

/** Icons */
import IconPlus from '@/assets/icons/icon-plus.svg'

/** Components */
import PodcastSectionBase from './PodcastSectionBase'
import AudioPlayer from '@/components/AudioPlayer.vue'
import VisualElementsRepeater from '@/components/VisualElementsRepeater.vue'
import VisualElementTypeImage from '@/components/VisualElementTypeImage.vue'
import VisualElementTypeAr from '@/components/VisualElementTypeAr.vue'
import VisualElementTypeLink from '@/components/VisualElementTypeLink.vue'
import VisualElementTypeLocation from '@/components/VisualElementTypeLocation.vue'
import VisualElementTypeQuote from '@/components/VisualElementTypeQuote.vue'
import VisualElementTypeVideo from '@/components/VisualElementTypeVideo.vue'
import VisualElementTypeQuiz from '@/components/VisualElementTypeQuiz.vue'
import VisualElementTypeGallery from '@/components/VisualElementTypeGallery.vue'
import VisualElementTypeMux from '@/components/VisualElementTypeMux.vue'

import {
  buildMutation,
  newId,
  isNew,
  updateCacheAfterSave,
  readQuery,
  updateCacheAfterDelete,
} from '@/lib/visualElements'

export default {
  extends: PodcastSectionBase,

  components: {
    AudioPlayer,
    VisualElementsRepeater,
    IconPlus,
    VisualElementTypeAr,
    VisualElementTypeImage,
    VisualElementTypeLink,
    VisualElementTypeLocation,
    VisualElementTypeQuote,
    VisualElementTypeVideo,
    VisualElementTypeQuiz,
    VisualElementTypeGallery,
    VisualElementTypeMux,
  },

  section: {
    query() {
      return {
        query: readQuery,
        variables: { podcastId: this.id },
      }
    },
    mutation() {
      const id = this.id
      const { mutation, variables } = buildMutation(
        Object.values(this.queryUpdateSegments),
        id,
      )

      const resetUpdateSegments = () => {
        this.queryUpdateSegments = {}
      }

      const vm = this

      return this.$apollo.mutate({
        mutation,
        variables,
        update(store, res) {
          vm.skipAutoSave = true
          const data = updateCacheAfterSave(store, res, id)
          vm.modelData.data = vm.sanitizeModelData(data).data.slice()
          resetUpdateSegments()
          vm.$nextTick(() => {
            vm.skipAutoSave = false

            // Open again the collapse that was open
            if (
              typeof vm.lastOpenIndex !== 'undefined' &&
              vm.lastOpenIndex >= 0
            ) {
              const openId = vm.modelData.data[vm.lastOpenIndex]._id
              if (openId) {
                vm.showRepeater(openId)
              }

              vm.lastOpenIndex = undefined
            }
          })
        },
      })
    },
    model: {
      data: [],
    },
    saveModel(data) {
      if (data.findPodcastByID.track) {
        this.trackUrl = data.findPodcastByID.track.url
      }

      data.findPodcastByID.visualElements.data = data.findPodcastByID.visualElements.data
        .slice()
        .sort((a, b) => a.time - b.time)

      return { ...data.findPodcastByID.visualElements }
    },
  },

  data() {
    return {
      trackUrl: '',
      currentTime: 0,
      queryUpdateSegments: {},
      validities: {},
      duration: 0,
    }
  },

  computed: {
    allValid() {
      return Object.values(this.validities).reduce(
        (currentValidity, totalValidity) => totalValidity && currentValidity,
        true,
      )
    },
    someContentIsExceeding() {
      return !!(
        this.modelData.data && this.modelData.data.some(this.getIsExceeding)
      )
    },
  },

  watch: {
    allValid(valid) {
      this.$store.commit('podcast/setValid', valid)
    },
  },

  methods: {
    findById(id) {
      return this.modelData.data.find(el => el._id === id)
    },

    findIndexById(id) {
      return this.modelData.data.findIndex(el => el._id === id)
    },
    updateVisualElementLabel(label, id) {
      const element = this.findById(id)
      if (element) {
        element.label = label
        this.updateVisualElement(element)
      }
    },

    updateVisualElementTime(event) {
      const element = this.findById(event.id)
      if (element) {
        element.time = parseFloat(event.time)
        this.updateVisualElement(element)
      }
    },

    updateVisualElementType(type, id) {
      const element = this.findById(id)
      if (element) {
        element.type = type
        element.content = {}
        this.updateVisualElement(element)
      }
    },

    updateVisualElement(data) {
      const clone = { ...data, content: { ...data.content } }
      const index = this.findIndexById(data._id)
      if (index >= 0) {
        this.$set(this.modelData.data, index, clone)
        this.modelData.data = this.modelData.data.slice()
        this.$set(this.queryUpdateSegments, clone._id, clone)
      }
    },

    reorderVisualElements() {
      this.modelData.data = this.modelData.data
        .slice()
        .sort((a, b) => a.time - b.time)
    },

    addVisualElement() {
      const element = {
        time: Math.round(this.currentTime),
        type: 'image',
        label: '',
        content: { src: '' },
        podcast: { connect: this.id },
        _id: newId(),
      }

      this.modelData.data.push(element)

      this.$set(this.queryUpdateSegments, element._id, element)
      this.$set(this.validities, element._id, false)

      if (this.$refs.repeaters) {
        this.$refs.repeaters.forEach(el => {
          el.$refs.repeaterCollapse.$refs.collapse.hide()
        })
      }

      this.reorderVisualElements()

      this.$nextTick(() => {
        this.showRepeater(element._id)
      })

      const index = this.findIndexById(element._id)
      if (index >= 0) {
        this.lastOpenIndex = index
      }
    },

    showRepeater(id) {
      const repeater = this.$refs.repeaters.find(rep => rep.element._id === id)
      if (repeater) {
        repeater.$refs.repeaterCollapse.$refs.collapse.show()
      } else {
        console.err("can't find visual element to show!")
      }
    },

    async deleteVisualElement(elementId) {
      if (confirm(`Cancellare definitivamente il contenuto?`)) {
        if (!isNew(elementId)) {
          try {
            const id = this.id
            await this.$apollo.mutate({
              mutation: DELETE_VISUAL_ELEMENT,
              variables: { id: elementId },
              update(store, res) {
                updateCacheAfterDelete(store, res, id)
              },
            })
          } catch (err) {
            console.log(err)
          }
        }

        this.skipDirtyCheck = true
        this.skipAutoSave = true

        const index = this.findIndexById(elementId)
        if (index !== -1) {
          this.modelData.data.splice(index, 1)
        }

        this.$delete(this.queryUpdateSegments, elementId)
        this.$delete(this.validities, elementId)

        this.$nextTick(() => {
          this.skipAutoSave = false
          this.skipDirtyCheck = false
        })
      }
    },

    updateValidity(valid, id) {
      this.$set(this.validities, id, valid)
    },

    afterUpdate(err) {
      if (err) {
        console.dir(err)
      } else {
        this.initialModelData = { ...this.modelData }
        this.markAsClean()
      }
    },

    getIsExceeding(visualElement) {
      return this.duration && visualElement.time > Math.ceil(this.duration)
    },
  },
}
</script>

<style lang="scss" scoped></style>
